/*
*   CRITRICAL Change:
*   1.  Removed Unnecessary API Call: The call to `fetchAssignmentSubmissions()` has been REMOVED from the `handleUpdatePosition` socket event handler. We don't need to be
*       fetching submission data when position updates!
*
*   Why:
*   -   The purpose of the 'updatePosition' event is ONLY to update the visual location of a student's icon on the canvas. It should ONLY modify the local `studentPositions` state.
*   -   Preventing Race Conditions: Fetching all assignment submissions every time a single student moves is extremely inefficient and was the direct cause of the "flash and disappear" bug that we saw in the library.
*       It was creating a Race Condition where this component would fetch old data and overwrite the new data that was correctly fetched by the parent component (`ClassroomCanvasViewer`).
*   -   By removing this call, we ensure that only the `ClassroomCanvasViewer` is responsible for fetching submission data, eliminating the state conflict and hopefully(!) improving performance.
*/

import React, { useEffect, useRef, useContext, useMemo, useState } from 'react';
import ReactFlow, { Background, useReactFlow } from 'reactflow';
import 'reactflow/dist/style.css';
import CanvasContext from '../../../context/CanvasContext';
import ClassroomCanvasNode from './ClassroomCanvasNode';
import ClassroomCanvasEdge from './ClassroomCanvasEdge';
import styles from './styles/ClassroomCanvas.module.css';
import socket from './../../../socket';

const ClassroomCanvas = () => {
    const {
        nodes,
        edges,
        loading,
        selectedNodeId,
        selectedEdgeId,
        onSelectNode,
        onSelectEdge,
        onCanvasClick,
    } = useContext(CanvasContext);

    const reactFlowWrapper = useRef(null);
    const { getZoom, setCenter } = useReactFlow();
    const [studentPositions, setStudentPositions] = useState({});

    useEffect(() => {
        const handleUpdatePosition = (data) => {
            //console.log("Received:", data);

            const { studentId, studentName, situationId } = data || {};

            if (!studentId || !studentName || !situationId) {
                //console.warn("Invalid:", data);
                return;
            }

            setStudentPositions((prev) => {
                const updated = Object.fromEntries(
                    Object.entries(prev).map(([nId, students]) => [
                        nId,
                        students.filter((s) => s.studentId !== studentId),
                    ])
                );

                updated[situationId] = [...(updated[situationId] || []), { studentId, studentName }];
                return updated;
            });
        };

        const handleStudentLeft = ({ userId }) => {
            setStudentPositions((prev) => {
                const updated = Object.fromEntries(
                    Object.entries(prev).map(([nId, students]) => [
                        nId,
                        students.filter((s) => s.studentId !== userId),
                    ])
                );
                return updated;
            });
        };

        socket.on('updatePosition', handleUpdatePosition);
        socket.on('studentLeft', handleStudentLeft);

        return () => {
            socket.off('updatePosition', handleUpdatePosition);
            socket.off('studentLeft', handleStudentLeft);
        };
    }, []);

    useEffect(() => {
        if (!loading && nodes.length > 0) {
            const startNode = nodes.find((node) => node.data?.endpointType === 'start');
            const nodeToCenter = startNode || nodes[0];

            if (nodeToCenter && nodeToCenter.position) {
                const { x, y } = nodeToCenter.position;
                const zoom = getZoom();
                setCenter(x, y, { zoom, duration: 800 });
            }
        }
    }, [loading, nodes, getZoom, setCenter]);

    const onNodeClick = (event, node) => {
        event.stopPropagation();
        if (selectedNodeId === node.id) {
            onCanvasClick();
        } else {
            onSelectNode(node.id);
        }
    };

    const onEdgeClick = (event, edge) => {
        event.stopPropagation();
        if (selectedEdgeId === edge.id) {
            onCanvasClick();
        } else {
            onSelectEdge(edge.id);
        }
    };

    const nodeTypes = useMemo(() => ({
        situation: (nodeProps) => {
            const isSelected = selectedNodeId === nodeProps.id;
            const students = studentPositions[nodeProps.id];
            return <ClassroomCanvasNode {...nodeProps} selected={isSelected} students={students}/>;
        },
    }), [selectedNodeId, studentPositions]);

    const edgeTypes = useMemo(() => ({
        choice: ClassroomCanvasEdge,
        smart: ClassroomCanvasEdge,
    }), []);

    if (loading) {
        return <div className={styles.loading}>Loading Canvas...</div>;
    }

    return (
        <div
            className={styles.container}
            ref={reactFlowWrapper}
            onDragOver={(event) => event.preventDefault()}
        >
            <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                onNodeClick={onNodeClick}
                onEdgeClick={onEdgeClick}
                onPaneClick={onCanvasClick}
                deleteKeyCode={null}
            >
                <Background color="#aaa" gap={16} />
            </ReactFlow>
        </div>
    );
};

export default ClassroomCanvas;